import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Plus,
  RefreshCw,
  FileText,
  Copy,
  Check,
  ExternalLink,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Building2,
  Upload,
  Download,
  FileSpreadsheet,
  Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
// Public URL for interview links (defaults to window.location.origin if not set)
const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || (typeof window !== 'undefined' ? window.location.origin : API);

export default function OrganizationApplications() {
  const { user, token } = useAuth();
  const csvFileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [createdApplication, setCreatedApplication] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [credits, setCredits] = useState(0);
  const [organizationName, setOrganizationName] = useState('');
  
  // CSV Import states
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  const [newApplication, setNewApplication] = useState({
    name: '',
    email: '',
    phone: '',
    passport_number: '',
    nic_number: '',
    university_id: '',
    campus: '',
    university_reference_number: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appsRes, uniRes, dashRes] = await Promise.all([
        axios.get(`${API}/api/organization/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/organization/universities`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/organization/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setApplications(appsRes.data);
      setUniversities(uniRes.data);
      setCredits(dashRes.data.credits || 0);
      
      // Auto-set the organization's university ID (only one option)
      if (uniRes.data.length > 0) {
        setNewApplication(prev => ({ ...prev, university_id: uniRes.data[0].id }));
        setOrganizationName(uniRes.data[0].name);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const handleCreateApplication = async (e) => {
    e.preventDefault();
    
    if (!newApplication.name || !newApplication.email || !newApplication.university_id) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (credits < 1) {
      toast.error('Insufficient credits. Please purchase more credits.');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/api/organization/applications`, newApplication, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreatedApplication(response.data);
      setShowCreateDialog(false);
      setShowSuccessDialog(true);
      fetchData();
      toast.success('Application created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create application');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    // Keep the university_id since it's always the organization's own ID
    setNewApplication(prev => ({
      name: '',
      email: '',
      phone: '',
      passport_number: '',
      nic_number: '',
      university_id: prev.university_id, // Keep the org's university ID
      campus: '',
      university_reference_number: '',
      notes: ''
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/10 text-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-500/10 text-blue-500"><Clock className="w-3 h-3 mr-1" />In Progress</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/10 text-red-500"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const downloadCSVTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/import/applications/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'applications_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCSVImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await axios.post(`${API}/api/import/applications/csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data.results);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import applications');
    } finally {
      setImporting(false);
    }
  };

  const sendApplicationEmail = async (appId) => {
    try {
      const response = await axios.post(`${API}/api/applications/${appId}/send-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="organization">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="organization">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">Applications</h2>
            <p className="text-muted-foreground">Create and manage student interview applications</p>
          </div>
          <div className="flex gap-2 items-center">
            <Badge className="bg-lime-500/10 text-lime-500 py-1 px-3">
              <span className="font-medium">{credits} Credits Available</span>
            </Badge>
            <Button 
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              data-testid="import-csv-applications-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button 
              onClick={() => {
                resetForm();
                setShowCreateDialog(true);
              }}
              className="bg-lime-500 hover:bg-lime-600"
              disabled={credits < 1}
              data-testid="create-application-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </div>
        </div>

        {/* Info Card */}
        {credits < 1 && (
          <Card className="border-amber-500/50 bg-amber-500/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-600">Insufficient Credits</p>
                  <p className="text-sm text-muted-foreground">
                    You need at least 1 credit to create an application. Each application costs 1 credit.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Applications Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">All Applications</CardTitle>
                <CardDescription>{applications.length} applications found</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {applications.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>University</TableHead>
                      <TableHead>Ref Number</TableHead>
                      <TableHead>Passport</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Campus</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((app) => (
                      <TableRow key={app.id} data-testid={`app-row-${app.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{app.name || app.student_name}</p>
                            <p className="text-xs text-muted-foreground">{app.email || app.student_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{app.university_name}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded font-mono">
                            {app.university_reference_number || '-'}
                          </code>
                        </TableCell>
                        <TableCell className="text-sm font-mono">{app.passport_number || '-'}</TableCell>
                        <TableCell className="text-sm">{app.phone || '-'}</TableCell>
                        <TableCell className="text-sm">{app.campus || '-'}</TableCell>
                        <TableCell>{getStatusBadge(app.status)}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {new Date(app.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(`${PUBLIC_URL}/interview/${app.interview_token}`)}
                              title="Copy interview link"
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(`${PUBLIC_URL}/interview/${app.interview_token}`, '_blank')}
                              title="Open interview"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => sendApplicationEmail(app.id)}
                              title="Send email to student"
                            >
                              <Mail className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No applications yet</p>
                <p className="text-sm mt-2">Create your first application to schedule an interview.</p>
                <Button 
                  className="mt-4" 
                  onClick={() => { resetForm(); setShowCreateDialog(true); }}
                  disabled={credits < 1}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Application
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Application Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-lime-500" />
              Create New Application
            </DialogTitle>
            <DialogDescription>
              Fill in the student details to create an interview application for {organizationName}. 1 credit will be deducted.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateApplication} className="space-y-4">
            {/* University Display (Read-only - Organization's own name) */}
            <div className="space-y-2">
              <Label>University</Label>
              <div className="flex items-center gap-2 p-3 bg-lime-500/10 border border-lime-500/20 rounded-lg">
                <Building2 className="w-5 h-5 text-lime-500" />
                <span className="font-medium text-lime-700 dark:text-lime-400">{organizationName}</span>
              </div>
              <p className="text-xs text-muted-foreground">Applications are created for your organization</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="app-name">Student Name *</Label>
                <Input
                  id="app-name"
                  placeholder="Full name"
                  value={newApplication.name}
                  onChange={(e) => setNewApplication({ ...newApplication, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app-email">Email *</Label>
                <Input
                  id="app-email"
                  type="email"
                  placeholder="student@email.com"
                  value={newApplication.email}
                  onChange={(e) => setNewApplication({ ...newApplication, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="app-phone">Phone</Label>
                <Input
                  id="app-phone"
                  placeholder="+44 123 456 7890"
                  value={newApplication.phone}
                  onChange={(e) => setNewApplication({ ...newApplication, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app-campus">Campus</Label>
                <Input
                  id="app-campus"
                  placeholder="Main Campus"
                  value={newApplication.campus}
                  onChange={(e) => setNewApplication({ ...newApplication, campus: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="app-passport">Passport Number</Label>
                <Input
                  id="app-passport"
                  placeholder="AB1234567"
                  value={newApplication.passport_number}
                  onChange={(e) => setNewApplication({ ...newApplication, passport_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app-nic">NIC Number</Label>
                <Input
                  id="app-nic"
                  placeholder="12345-1234567-1"
                  value={newApplication.nic_number}
                  onChange={(e) => setNewApplication({ ...newApplication, nic_number: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="app-ref">University Reference Number</Label>
              <Input
                id="app-ref"
                placeholder="REF-2024-001"
                value={newApplication.university_reference_number}
                onChange={(e) => setNewApplication({ ...newApplication, university_reference_number: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="app-notes">Notes</Label>
              <Textarea
                id="app-notes"
                placeholder="Any additional notes..."
                value={newApplication.notes}
                onChange={(e) => setNewApplication({ ...newApplication, notes: e.target.value })}
                rows={2}
              />
            </div>

            <div className="bg-lime-50 dark:bg-lime-900/20 border border-lime-200 dark:border-lime-800 rounded-lg p-3">
              <p className="text-sm">
                <strong>Cost:</strong> 1 credit • <strong>Remaining after:</strong> {credits - 1} credits
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || credits < 1} className="bg-lime-500 hover:bg-lime-600">
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Application
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-5 h-5" />
              Application Created!
            </DialogTitle>
          </DialogHeader>
          
          {createdApplication && (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Student:</span>
                  <span className="font-medium">{createdApplication.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">University:</span>
                  <span className="font-medium">{createdApplication.university_name}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Interview Link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${PUBLIC_URL}/interview/${createdApplication.interview_token}`}
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(`${PUBLIC_URL}/interview/${createdApplication.interview_token}`)}
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Share this link with the student to start their interview.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => { setShowSuccessDialog(false); setCreatedApplication(null); }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CSV Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-lime-500" />
              Import Applications from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to create multiple applications at once. Each application will use 1 credit.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Download Template */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Download CSV Template</p>
                  <p className="text-xs text-muted-foreground">Required columns: Reference Number, Campus, Passport Number, Student Name, Email, Phone, University</p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadCSVTemplate}>
                  <Download className="w-4 h-4 mr-2" />
                  Template
                </Button>
              </div>
            </div>
            
            {/* File Upload */}
            <div>
              <Label>Upload CSV File</Label>
              <div 
                className={`mt-2 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  csvFile ? 'border-lime-500 bg-lime-500/5' : 'border-muted-foreground/25 hover:border-lime-500/50'
                }`}
                onClick={() => csvFileInputRef.current?.click()}
              >
                <input
                  ref={csvFileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    setCsvFile(e.target.files[0]);
                    setImportResults(null);
                  }}
                />
                {csvFile ? (
                  <div>
                    <FileSpreadsheet className="w-10 h-10 mx-auto text-lime-500 mb-2" />
                    <p className="font-medium">{csvFile.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">Click to change file</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                    <p className="font-medium">Click to upload CSV</p>
                    <p className="text-xs text-muted-foreground">or drag and drop</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Available Credits Warning */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <p className="text-sm text-amber-600">
                  You have <strong>{credits}</strong> credits available. Each application costs 1 credit.
                </p>
              </div>
            </div>
            
            {/* Import Results */}
            {importResults && (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <p className="text-sm font-medium text-green-600">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      {importResults.success?.length || 0} Imported
                    </p>
                  </div>
                  <div className="flex-1 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-600">
                      <XCircle className="w-4 h-4 inline mr-1" />
                      {importResults.failed?.length || 0} Failed
                    </p>
                  </div>
                </div>
                
                {importResults.emails_sent > 0 && (
                  <p className="text-sm text-muted-foreground">
                    <Mail className="w-4 h-4 inline mr-1" />
                    {importResults.emails_sent} emails sent to students
                  </p>
                )}
                
                {importResults.failed?.length > 0 && (
                  <div className="max-h-32 overflow-y-auto bg-red-500/5 rounded p-2">
                    {importResults.failed.map((f, i) => (
                      <p key={i} className="text-xs text-red-600">
                        Row {f.row}: {f.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowImportDialog(false);
              setCsvFile(null);
              setImportResults(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCSVImport}
              disabled={!csvFile || importing}
              className="bg-lime-500 hover:bg-lime-600"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Applications
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
